import {createContext, useContext} from 'react';

/**
 * Utility functions and context for Popover component
 */

/**
 * Popover context for sharing state between Popover components
 */
export const PopoverContext = createContext(null);

/**
 * Global registry of open popovers for nested detection
 * Maps popover ID to { popperElement, referenceElement, parentId }
 */
const openPopovers = new Map();

/**
 * Hook to access Popover context
 * @throws {Error} If used outside of Popover provider
 */
export const usePopoverContext = () => {
	const context = useContext(PopoverContext);
	if (!context) {
		throw new Error('Popover components must be used within a Popover provider');
	}
	return context;
};

/**
 * Register an open popover
 */
export const registerPopover = (id, popperElement, referenceElement, parentId = null) => {
	if (popperElement) {
		openPopovers.set(id, {popperElement, referenceElement, parentId});
	}
};

/**
 * Unregister a popover
 */
export const unregisterPopover = id => {
	openPopovers.delete(id);
};

/**
 * Find parent popover ID by checking if reference element is inside another popover
 */
export const findParentPopoverId = referenceElement => {
	if (!referenceElement) return null;

	for (const [id, {popperElement}] of openPopovers.entries()) {
		if (popperElement && popperElement.contains(referenceElement)) {
			return id;
		}
	}

	return null;
};

/**
 * Check if click target is inside a nested child popover
 */
export const isClickInsideNestedPopover = (clickTarget, thisPopoverId) => {
	if (!clickTarget) return false;

	const thisPopover = openPopovers.get(thisPopoverId);
	if (!thisPopover) return false;

	// Check all other popovers to see if any are children of this one
	for (const [id, popover] of openPopovers.entries()) {
		if (id === thisPopoverId) continue;
		// Check if this other popover is a child (its parentId is our id)
		if (popover.parentId === thisPopoverId) {
			// Check if click is inside this child's popper
			if (popover.popperElement && popover.popperElement.contains(clickTarget)) {
				return true; // Click is inside a nested child popover
			}
		}
	}

	return false;
};

/**
 * Check if click target is inside a parent popover
 */
export const isClickInsideParentPopover = (clickTarget, thisPopoverId) => {
	if (!clickTarget) return false;

	const thisPopover = openPopovers.get(thisPopoverId);
	if (!thisPopover || !thisPopover.parentId) return false;

	const parentPopover = openPopovers.get(thisPopover.parentId);
	if (!parentPopover) return false;

	// Check if click is inside parent's popper or reference element
	return (
		(parentPopover.popperElement && parentPopover.popperElement.contains(clickTarget)) ||
		(parentPopover.referenceElement &&
			parentPopover.referenceElement.contains(clickTarget))
	);
};

/**
 * Default ignore selectors for outside click detection
 * These prevent popovers from closing when interacting with nested components
 */
export const defaultIgnoreSelectors = [
	'[data-multiselect-menu="true"]',
	'[data-typeahead-menu="true"]',
	'[data-popover-menu="true"]',
];
